import * as React from "react";
import { cn } from "../../lib/utils";

function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("animate-pulse rounded-md bg-skeleton", className)}
      aria-hidden="true"
      {...props}
    />
  );
}

function SkeletonText({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div
      className={cn("space-y-2", className)}
      role="status"
      aria-label="Loading"
    >
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn("h-4", i === lines - 1 ? "w-2/3" : "w-full")}
        />
      ))}
    </div>
  );
}

function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      className={cn("rounded-lg bg-card p-6 space-y-4", className)}
      role="status"
      aria-label="Loading"
    >
      {/* Header: avatar + name + actions — mirrors the real card header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-7 w-7 shrink-0 rounded-md" />
          <Skeleton className="h-4 w-28" />
        </div>
        <Skeleton className="h-7 w-7 rounded-md" />
      </div>
      {/* Description */}
      <div className="space-y-2">
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-3/4" />
      </div>
      {/* Env badges */}
      <div className="flex gap-1">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-5 w-14 rounded-full" />
      </div>
      {/* Footer: health + updated */}
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-1.5">
          <Skeleton className="h-2 w-2 rounded-full" />
          <Skeleton className="h-3 w-20" />
        </div>
        <Skeleton className="h-3 w-14" />
      </div>
    </div>
  );
}

function SkeletonRow({ className }: { className?: string }) {
  return (
    <div
      className={cn("flex items-center gap-3 px-4 py-3 sm:gap-4", className)}
      role="status"
      aria-label="Loading"
    >
      {/* Avatar */}
      <Skeleton className="h-8 w-8 shrink-0 rounded-md" />

      {/* Name + mobile sub-line */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-48 sm:hidden" />
      </div>

      {/* Description — sm+ */}
      <Skeleton className="hidden h-4 w-48 shrink-0 sm:block lg:w-60" />

      {/* Env badges — lg+ */}
      <div className="hidden w-36 shrink-0 items-center gap-1 lg:flex">
        <Skeleton className="h-5 w-14 rounded-full" />
        <Skeleton className="h-5 w-14 rounded-full" />
      </div>

      {/* Health — sm+ */}
      <div className="hidden w-36 shrink-0 items-center gap-2 sm:flex">
        <Skeleton className="h-2 w-2 rounded-full" />
        <Skeleton className="h-3 w-20" />
      </div>

      {/* Updated — lg+ */}
      <Skeleton className="hidden h-3 w-14 shrink-0 lg:block" />

      {/* Actions */}
      <Skeleton className="h-8 w-8 shrink-0 rounded-md" />
    </div>
  );
}

function SkeletonList({
  count = 3,
  className,
}: {
  count?: number;
  className?: string;
}) {
  return (
    <div className={cn("grid gap-6 md:grid-cols-2 lg:grid-cols-3", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

export { Skeleton, SkeletonText, SkeletonCard, SkeletonRow, SkeletonList };
